'use client'

import Script from 'next/script'

import serializeForScriptTag from '@/helpers/serializeForScriptTag'
import type { SearchResult } from '@/queries/listingSearchQuery'

import { generateProductSchemas } from './data/productSchema'

interface ProductStructuredDataProps {
  listings: SearchResult[]
  baseUrl: string
  nonce?: string
}

/**
 * Component that generates and injects Product JSON-LD structured data
 * for the first three listings displayed on the page
 */
const ProductStructuredData = ({
  listings,
  baseUrl,
  nonce,
}: ProductStructuredDataProps) => {
  // Derived from props, so computed during render rather than mirrored into
  // state from an effect: the effect version rendered `null` first and only
  // produced the markup on a second pass.
  //
  // Note this still is not in the server-rendered HTML — `listings` comes from
  // the client-side listing-search context, so there is nothing to serialize
  // during SSR. Getting product JSON-LD in front of non-JS crawlers needs the
  // listings fetched server-side, which is a larger change.
  const productSchemas =
    listings.length > 0 ? generateProductSchemas(listings, baseUrl) : []

  if (productSchemas.length === 0) {
    return null
  }

  return (
    <>
      {productSchemas.map((schema, index) => (
        <Script
          key={`product-structured-data-${listings[index]?.id || index}`}
          id={`product-structured-data-${index}`}
          nonce={nonce}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: serializeForScriptTag(schema, 2),
          }}
        />
      ))}
    </>
  )
}

export default ProductStructuredData
